import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import Pagination from "../../utils/pagination";
import Header from "../../components/content_wrapper/header";
import { useUpdateOwner } from "../../hooks/ownerMangementApi/updateOwner";
import { useGetOwnerList } from "../../hooks/ownerMangementApi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUpdateOwnerStatus } from "../../hooks/ownerMangementApi/updateStatus";
import { EditSvg, EyeSvg } from "../../utils/svgComponent";
import { useFormik } from "formik";
import {
  editOwnerInitialValues,
  editOwnerValidationSchema,
  resetPasswordSchema,
  resetPasswordValues,
} from "../../validations";
import { useOwnerResetPassword } from "../../hooks/auth/ownerResetPass";
import CustomLaoder from "../../components/customLoader";
import NoRecordFound from "../../components/noRecordFound";

const OwnersListingScreen: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get("search") || "";
  const initialPage: any = parseInt(searchParams.get("page") || "1", 10);
  const initialLimit = parseInt(searchParams.get("limit") || "20", 10);

  // Debounced live search — no Apply/Cancel needed
  const [searchInput, setSearchInput] = useState<string>(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState<string>(initialSearch);

  // Status filter: '1' = Active, '2' = Suspended, 'all' = All
  const [statusFilter, setStatusFilter] = useState<string>("1");

  const [pageNumber, setPageNumber] = useState<any>(initialPage);
  const [countData, setCountData] = useState<any>(initialLimit);
  const [ownerId, setOwnerId] = useState<any>();
  const [owner, setOwner] = useState<any>();
  const [showEye, setShowEye] = useState(false);
  const [showConfirmEye, setShowConfirmEye] = useState(false);
  // Single modal — toggle between "edit" and "reset password" panels
  const [resetPasswordMode, setResetPasswordMode] = useState(false);

  // View mode: card grid or table
  const [viewMode, setViewMode] = useState<"cards" | "table">("table");
  // Which card's ⋮ dropdown is open (by owner Id)
  const [_openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  // Close any open dropdown when user clicks elsewhere
  useEffect(() => {
    const close = () => setOpenDropdownId(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  const navigate = useNavigate();
  const { mutateAsync } = useUpdateOwnerStatus();
  const { mutateAsync: resetPassword, isLoading: resetLoading } = useOwnerResetPassword();

  // Debounce search: wait 400 ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPageNumber(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleToggleClick = async (id: string, action: number) => {
    const Status = action === 1 ? 2 : 1;
    if (Status === 2) {
      Swal.fire({
        title: "Are you sure?",
        text: "You want to suspend this Owner?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#72bcb8",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, Suspend",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const res: any = await mutateAsync({ ownerId: id, userStatus: Status });
            if (!res.success) console.error("Failed to update owner status:", res.error);
          } catch (error: any) {
            console.error("Unexpected error:", error);
          }
        }
      });
    } else {
      try {
        const res: any = await mutateAsync({ ownerId: id, userStatus: Status });
        if (!res.success) console.error("Failed to update owner status:", res.error);
      } catch (error: any) {
        console.error("Unexpected error:", error);
      }
    }
  };

  const {
    data: OwnerListing,
    isError,
    isLoading,
  } = useGetOwnerList({
    page: pageNumber,
    limit: countData,
    search: debouncedSearch,
    status: statusFilter,
  });

  const handlePrevPage = () => setPageNumber((p: any) => p - 1);
  const handleNextPage = () => setPageNumber((p: any) => p + 1);
  const handleRowLimitChange = (newValue: number) => setCountData(newValue);

  const handleClick = (id: any) => {
    navigate("/ownerdetail", { state: { id } });
  };

  /* ── Edit owner form ── */
  const { mutateAsync: updateOwner } = useUpdateOwner();
  const {
    values,
    touched,
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    setValues,
  } = useFormik({
    initialValues: editOwnerInitialValues,
    validationSchema: editOwnerValidationSchema,
    onSubmit: async () => {
      try {
        const res: any = await updateOwner({
          id: owner?._id,
          name: values.username,
        });
        if (res?.status === true) {
          const closeModelButton: any = document.getElementById("close-btn");
          closeModelButton?.click();
          toast.success(res?.message);
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message);
      }
    },
  });

  const handleEditOwner = (id: any) => {
    setOwnerId(id);
    setResetPasswordMode(false);
  };

  useEffect(() => {
    if (OwnerListing?.data && ownerId) {
      const found = OwnerListing.data.keyOwners.find(
        (o: any) => o._id === ownerId
      );
      if (found) {
        setOwner(found);
        setValues({
          countryCode: found?.countryCode || "",
          phoneNumber: found?.phoneNumber ? JSON.stringify(found.phoneNumber) : "",
          username: found?.name || "",
        });
      }
    }
  }, [OwnerListing, ownerId, setValues]);

  /* ── Reset password form ── */
  const {
    values: reValues,
    touched: reTouches,
    errors: reErrors,
    handleBlur: reHandleBlur,
    handleChange: reHandleChange,
    handleSubmit: reHandleSubmit,
    resetForm: reset,
  } = useFormik({
    initialValues: resetPasswordValues,
    validationSchema: resetPasswordSchema,
    onSubmit: async () => {
      try {
        const res: any = await resetPassword({
          user_id: ownerId,
          password: reValues.confirmPassword,
        });
        if (res?.status === true) {
          toast.success(res?.message);
          reset();
          setResetPasswordMode(false);
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message);
      }
    },
  });

  const owners = OwnerListing?.data?.keyOwners ?? [];
  const isEmpty = !isLoading && (owners.length === 0 || isError);

  return (
    <div className="main-wrapper">
      <Header title="Owners" />
      <CustomLaoder loader={isLoading}>
        <div className="content-wrapper">
          <section className="main-section coach-section spacer">
            <div className="container">
              <div className="wrapper-card">
                <div className="row gy-3">

                  {/* ── Live search bar + view toggle ── */}
                  <div className="col-xl-12">
                    <div className="d-flex align-items-center gap-3">
                      {/* Search takes all remaining space */}
                      <div
                        className="form-block p-relative flex-grow-1"
                        style={{ position: "relative" }}
                      >
                        <input
                          type="text"
                          className="form-control"
                          id="search"
                          placeholder="Search by name or email…"
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                          autoComplete="off"
                        />
                        <span className="search-icon" />

                        {/* Inline clear button */}
                        {searchInput && (
                          <button
                            type="button"
                            onClick={() => setSearchInput("")}
                            title="Clear search"
                            aria-label="Clear search"
                            style={{
                              position: "absolute",
                              right: "10px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              background: "none",
                              border: "none",
                              padding: 0,
                              cursor: "pointer",
                              color: "#aaa",
                              fontSize: "14px",
                              lineHeight: 1,
                            }}
                          >
                            <i className="fa-solid fa-circle-xmark" />
                          </button>
                        )}

                        {/* Micro-spinner while debounce is pending */}
                        {isLoading && (
                          <span
                            className="spinner-border spinner-border-sm text-secondary"
                            style={{
                              position: "absolute",
                              right: searchInput ? "34px" : "10px",
                              top: "50%",
                              transform: "translateY(-50%)",
                            }}
                          />
                        )}
                      </div>

                      {/* Card / Table view toggle */}
                      <div className="d-flex gap-2 flex-shrink-0">
                        <button
                          type="button"
                          className={`btn btn-sm ${
                            viewMode === "cards"
                              ? "btn-primary"
                              : "btn-outline-secondary"
                          }`}
                          onClick={() => setViewMode("cards")}
                          title="Card View"
                        >
                          <i className="fa-solid fa-grip" />
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${
                            viewMode === "table"
                              ? "btn-primary"
                              : "btn-outline-secondary"
                          }`}
                          onClick={() => setViewMode("table")}
                          title="Table View"
                        >
                          <i className="fa-solid fa-list" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* ── Status filter pills + result count ── */}
                  <div className="col-xl-12">
                    <div
                      className="d-flex align-items-center gap-2"
                      style={{ fontSize: "0.875rem" }}
                    >
                      <span className="text-muted me-1">Show:</span>
                      {(
                        [
                          { label: "● Active",    value: "1"   },
                          { label: "○ Suspended", value: "2"   },
                          { label: "All",          value: "all" },
                        ] as { label: string; value: string }[]
                      ).map((f) => (
                        <button
                          key={f.value}
                          type="button"
                          onClick={() => {
                            setStatusFilter(f.value);
                            setPageNumber(1);
                          }}
                          style={{
                            borderRadius: "999px",
                            padding: "3px 14px",
                            fontSize: "0.8rem",
                            fontWeight: statusFilter === f.value ? 600 : 400,
                            cursor: "pointer",
                            border: `1px solid ${
                              f.value === "1"
                                ? "#198754"
                                : f.value === "2"
                                ? "#dc3545"
                                : "#6c757d"
                            }`,
                            backgroundColor:
                              statusFilter === f.value
                                ? f.value === "1"
                                  ? "#198754"
                                  : f.value === "2"
                                  ? "#dc3545"
                                  : "#6c757d"
                                : "transparent",
                            color:
                              statusFilter === f.value
                                ? "#fff"
                                : f.value === "1"
                                ? "#198754"
                                : f.value === "2"
                                ? "#dc3545"
                                : "#6c757d",
                            transition: "all 0.15s",
                          }}
                        >
                          {f.label}
                        </button>
                      ))}

                      {OwnerListing?.data?.totalRecord !== undefined && (
                        <span
                          className="text-muted ms-2"
                          style={{ fontSize: "0.78rem" }}
                        >
                          {OwnerListing.data.totalRecord}{" "}
                          result{OwnerListing.data.totalRecord !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* ── Owners listing — card or table view ── */}
                  <div className="col-xl-12">
                    <div className="sub-heading">
                      <h2>List of Owners</h2>
                    </div>

                    {/* ── CARD VIEW ── */}
                    {viewMode === "cards" && (
                      <>
                        {isEmpty ? (
                          <NoRecordFound />
                        ) : (
                          <div className="row gy-4 mt-1">
                            {owners.map((items: any, i: number) => (
                              <div
                                key={i}
                                className="col-12 col-sm-6 col-lg-4 col-xl-3"
                              >
                                <div
                                  className="card h-100 shadow-sm"
                                  style={{ borderRadius: 12 }}
                                >
                                  <div
                                    className="card-body"
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      textAlign: "center",
                                      padding: "24px 16px 16px",
                                    }}
                                  >
                                    {/* Name */}
                                    <p
                                      style={{
                                        color: "#212529",
                                        fontWeight: 600,
                                        fontSize: "0.95rem",
                                        marginBottom: 2,
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {items?.name || "—"}
                                    </p>

                                    {/* Email */}
                                    <p
                                      style={{
                                        color: "#6c757d",
                                        fontSize: "0.8rem",
                                        marginBottom: 4,
                                        wordBreak: "break-all",
                                      }}
                                    >
                                      {items?.email || "—"}
                                    </p>

                                    {/* Phone */}
                                    {(items?.countryCode || items?.phoneNumber) && (
                                      <p style={{ color: "#6c757d", fontSize: "0.78rem", marginBottom: 4 }}>
                                        {items.countryCode ? `+${items.countryCode} ` : ""}{items.phoneNumber || ""}
                                      </p>
                                    )}

                                    {/* Key count + joined */}
                                    <div style={{ display: "flex", gap: 12, marginBottom: 12, fontSize: "0.78rem", color: "#6c757d" }}>
                                      <span>
                                        <i className="fa-solid fa-key me-1" style={{ color: "#72bcb8" }} />
                                        {items.keyCount ?? 0} {(items.keyCount ?? 0) === 1 ? "key" : "keys"}
                                      </span>
                                      {items.createdAt && (
                                        <span>
                                          <i className="fa-regular fa-calendar me-1" />
                                          {new Date(items.createdAt).toLocaleDateString(undefined, { year: "numeric", month: "short" })}
                                        </span>
                                      )}
                                    </div>

                                    {/* Status badge */}
                                    <span
                                      className={`c-pill ${
                                        items?.userStatus === 1
                                          ? "c-pill--success"
                                          : "c-pill--danger"
                                      }`}
                                      style={{ marginBottom: 16 }}
                                    >
                                      {items?.userStatus === 1
                                        ? "Active"
                                        : "Suspended"}
                                    </span>

                                    {/* Action row */}
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 16,
                                        borderTop: "1px solid #e9ecef",
                                        paddingTop: 12,
                                        width: "100%",
                                        marginTop: "auto",
                                      }}
                                    >
                                      {/* View */}
                                      <button
                                        type="button"
                                        style={{
                                          background: "none",
                                          border: "none",
                                          padding: 0,
                                          cursor: "pointer",
                                        }}
                                        onClick={() => handleClick(items?._id)}
                                        title="View Details"
                                      >
                                        <EyeSvg />
                                      </button>

                                      {/* Toggle */}
                                      <span className="table-check">
                                        <input
                                          type="checkbox"
                                          id={`card-toggle${items._id}`}
                                          className="toggle-input"
                                          onChange={() =>
                                            handleToggleClick(
                                              items._id,
                                              items?.userStatus
                                            )
                                          }
                                          checked={items?.userStatus === 1}
                                        />
                                        <label
                                          htmlFor={`card-toggle${items._id}`}
                                          className="toggle-label"
                                          title={
                                            items?.userStatus === 1
                                              ? "Suspend"
                                              : "Activate"
                                          }
                                        />
                                      </span>

                                      {/* Edit */}
                                      <button
                                        type="button"
                                        style={{
                                          background: "none",
                                          border: "none",
                                          padding: 0,
                                          cursor: "pointer",
                                        }}
                                        data-bs-toggle="modal"
                                        data-bs-target="#editOwnerDetail"
                                        onClick={() =>
                                          handleEditOwner(items._id)
                                        }
                                        title="Edit Owner"
                                      >
                                        <EditSvg />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        <Pagination
                          totalPages={OwnerListing?.data?.totalPage}
                          currentPage={pageNumber}
                          handlePrevPage={handlePrevPage}
                          handleNextPage={handleNextPage}
                          handleRowLimitChange={handleRowLimitChange}
                        />
                      </>
                    )}

                    {/* ── TABLE VIEW ── */}
                    {viewMode === "table" && (
                      <div className="table-blk table-responsive">
                        <table id="myTable" className="display border-0">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Phone</th>
                              <th>Keys</th>
                              <th>Joined</th>
                              <th>Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {owners.map((items: any, i: number) => (
                              <tr key={i}>
                                <td>
                                  <span className="table-profile">
                                    <span className="table-profileimg">
                                      <img
                                        src={
                                          items.profileImage
                                            ? `${process.env.REACT_APP_ASSETS_URL}/uploads/profileImages/${items.profileImage}`
                                            : "assets/images/icons/dummy-user.png"
                                        }
                                        alt="user"
                                        onError={(e) => {
                                          e.currentTarget.src =
                                            "assets/images/icons/dummy-user.png";
                                        }}
                                      />
                                    </span>
                                    <span className="table-username">
                                      {items?.name}
                                    </span>
                                  </span>
                                </td>
                                <td>{items.email}</td>
                                <td style={{ whiteSpace: "nowrap" }}>
                                  {items.countryCode && items.phoneNumber
                                    ? `+${items.countryCode} ${items.phoneNumber}`
                                    : <span className="text-muted">—</span>}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      minWidth: 24,
                                      fontWeight: 600,
                                      color: items.keyCount > 0 ? "#212529" : "#adb5bd",
                                    }}
                                  >
                                    {items.keyCount ?? 0}
                                  </span>
                                </td>
                                <td style={{ whiteSpace: "nowrap", fontSize: "0.82rem", color: "#6c757d" }}>
                                  {items.createdAt
                                    ? new Date(items.createdAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
                                    : <span className="text-muted">—</span>}
                                </td>
                                <td>
                                  <span
                                    className={`c-pill ${
                                      items?.userStatus === 1
                                        ? "c-pill--success"
                                        : "c-pill--danger"
                                    }`}
                                  >
                                    {items?.userStatus === 1
                                      ? "Active"
                                      : "Suspended"}
                                  </span>
                                </td>
                                <td>
                                  <span className="table-action">
                                    {/* View details */}
                                    <button
                                      type="button"
                                      className="table-eye"
                                      style={{
                                        cursor: "pointer",
                                        background: "none",
                                        border: "none",
                                        padding: 0,
                                      }}
                                      onClick={() => handleClick(items?._id)}
                                      title="View Details"
                                    >
                                      <EyeSvg />
                                    </button>

                                    {/* Activate / Suspend toggle */}
                                    <span className="table-check">
                                      <input
                                        type="checkbox"
                                        id={`toggle${items._id}`}
                                        className="toggle-input"
                                        onChange={() =>
                                          handleToggleClick(
                                            items._id,
                                            items?.userStatus
                                          )
                                        }
                                        checked={items?.userStatus === 1}
                                      />
                                      <label
                                        htmlFor={`toggle${items._id}`}
                                        className="toggle-label"
                                        title={
                                          items?.userStatus === 1
                                            ? "Suspend"
                                            : "Activate"
                                        }
                                      />
                                    </span>

                                    {/* Edit */}
                                    <button
                                      type="button"
                                      className="table-edit"
                                      style={{
                                        cursor: "pointer",
                                        background: "none",
                                        border: "none",
                                        padding: 0,
                                      }}
                                      data-bs-toggle="modal"
                                      data-bs-target="#editOwnerDetail"
                                      onClick={() => handleEditOwner(items._id)}
                                      title="Edit Owner"
                                    >
                                      <EditSvg />
                                    </button>
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {/* Empty state */}
                        {isEmpty && <NoRecordFound />}

                        <Pagination
                          totalPages={OwnerListing?.data?.totalPage}
                          currentPage={pageNumber}
                          handlePrevPage={handlePrevPage}
                          handleNextPage={handleNextPage}
                          handleRowLimitChange={handleRowLimitChange}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </CustomLaoder>

      {/* ── Edit / Reset-Password Modal (single modal, two panels) ── */}
      <div
        className="modal fade common-modal"
        id="editOwnerDetail"
        tabIndex={-1}
        aria-labelledby="editOwnerDetailLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <button
                type="button"
                id="close-btn"
                className="btn-close"
                data-bs-dismiss="modal"
                onClick={() => {
                  setResetPasswordMode(false);
                  reset();
                }}
              />

              <div className="common-box-body">
                {/* ── Panel 1: Edit Owner ── */}
                {!resetPasswordMode && (
                  <>
                    <h2>Edit Owner</h2>
                    <div className="common-modal-form">
                      <div className="d-flex justify-content-end mb-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setResetPasswordMode(true)}
                        >
                          <i className="fa-solid fa-lock me-1" />
                          Reset Password
                        </button>
                      </div>
                      <div className="common-modal-field">
                        <label>Username</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter username"
                          name="username"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.username}
                        />
                        {touched.username && errors.username && (
                          <p className="error">{errors.username}</p>
                        )}
                      </div>
                    </div>
                    <div className="common-modal-btns">
                      <button
                        className="btn primary-btn"
                        onClick={() => handleSubmit()}
                      >
                        Submit
                      </button>
                    </div>
                  </>
                )}

                {/* ── Panel 2: Reset Password ── */}
                {resetPasswordMode && (
                  <>
                    <div className="d-flex align-items-center mb-3">
                      <button
                        type="button"
                        className="btn btn-sm btn-light me-2"
                        onClick={() => {
                          setResetPasswordMode(false);
                          reset();
                        }}
                        title="Back to Edit"
                      >
                        <i className="fa-solid fa-arrow-left" />
                      </button>
                      <h2 className="mb-0">Reset Password</h2>
                    </div>

                    <div className="common-modal-form">
                      <div className="common-modal-field">
                        <label htmlFor="psw">New Password</label>
                        <div className="p-relative">
                          <span className="eye">
                            <i
                              className={
                                !showEye
                                  ? "fa-regular fa-eye"
                                  : "fa-regular fa-eye-slash"
                              }
                              onClick={() => setShowEye(!showEye)}
                            />
                          </span>
                          <input
                            id="psw"
                            maxLength={10}
                            type={!showEye ? "password" : "text"}
                            className="form-control"
                            placeholder="New password"
                            value={reValues?.newPassword}
                            onChange={reHandleChange}
                            onBlur={reHandleBlur}
                            name="newPassword"
                          />
                          {reTouches.newPassword && reErrors.newPassword && (
                            <p className="error">{reErrors.newPassword}</p>
                          )}
                        </div>
                      </div>

                      <div className="common-modal-field">
                        <label htmlFor="cpsw">Confirm Password</label>
                        <div className="p-relative">
                          <span className="eye">
                            <i
                              className={
                                !showConfirmEye
                                  ? "fa-regular fa-eye"
                                  : "fa-regular fa-eye-slash"
                              }
                              onClick={() => setShowConfirmEye(!showConfirmEye)}
                            />
                          </span>
                          <input
                            id="cpsw"
                            maxLength={10}
                            type={!showConfirmEye ? "password" : "text"}
                            className="form-control"
                            placeholder="Confirm password"
                            onChange={reHandleChange}
                            onBlur={reHandleBlur}
                            value={reValues?.confirmPassword}
                            name="confirmPassword"
                          />
                          {reTouches.confirmPassword &&
                            reErrors.confirmPassword && (
                              <p className="error">
                                {reErrors.confirmPassword}
                              </p>
                            )}
                        </div>
                      </div>
                    </div>

                    <div className="common-modal-btns">
                      <button
                        className="btn primary-btn"
                        onClick={() => reHandleSubmit()}
                      >
                        {resetLoading ? "Saving…" : "Save Password"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnersListingScreen;
